import { createContext } from 'react';
import type { Ability } from './permissions';
import type { SupportSessionInfo } from './session';
import type { User } from './types';

export interface AuthContextValue {
  /**
   * The identity every screen should render and gate on.
   *
   * ⚠️ **During a support session this is the TARGET, not the super admin**, and
   * it is loaded from `/auth/me` under the support token rather than taken from
   * the start response. `ability` is built from this user and the TARGET's
   * `/permissions/me`, so gating follows the person being supported — which is
   * the entire point of the feature. To ask "who really started this", read
   * `supportSession.startedBy`; there is no other copy of the super admin's
   * identity on this context, deliberately, so nothing can gate on it by
   * accident.
   */
  user: User | null;
  /** True until the initial "restore session from stored token" check finishes. */
  loading: boolean;
  /**
   * What this user may be offered. Never gate on `user.role` directly — the
   * rule reconciling roles with `/permissions/me` lives in `permissions.ts`,
   * and the two disagree in both directions.
   */
  ability: Ability;
  /** `remember` decides whether the session outlives the browser. */
  login: (email: string, password: string, remember: boolean) => Promise<void>;
  /** Clears local state immediately and revokes the refresh token in the background. */
  logout: () => void;
  /**
   * Re-reads the signed-in user from `/auth/me`.
   *
   * Needed because an admin can edit their own account on `/users`: without
   * this the account menu keeps showing the old name, email or role until a
   * reload. `/auth/me` reads the database rather than decoding the token, so it
   * also picks up a role change without re-authenticating.
   *
   * Never signs anyone out — a failure leaves the current user in place, since
   * a transient error here is not evidence the session ended.
   */
  refreshUser: () => Promise<void>;

  /**
   * The support session in progress, or null when acting as yourself.
   *
   * Null is by far the common case — check it before reading anything else.
   * While it is non-null, `user` and `ability` describe `actingAs`, and every
   * request this app makes carries the support token.
   */
  supportSession: SupportSessionInfo | null;

  /**
   * Milliseconds left on that session, or `NaN` when there is none to measure.
   *
   * ⚠️ **A FUNCTION, not a field, and that is the point.** A number on
   * {@link supportSession} would be a snapshot taken when the context last
   * rendered, so a banner counting down would either re-render the whole tree
   * once a minute to refresh it or quietly display a stale figure. This
   * recomputes on call, so the caller owns its own tick.
   *
   * ⚠️ **SKEW-FREE, and it must stay that way.** It measures against the
   * deadline the provider derived from the server's remaining DURATION
   * (`expiresIn`) at the moment the session started — never by parsing
   * `expiresAt`, which is the server's instant compared against the browser's
   * clock and lands the whole difference between them on the user. A browser
   * five minutes fast would end a 30-minute session at 25; five minutes slow
   * would keep the banner claiming an identity the server stopped honouring.
   * `supportSession.expiresAt` is for DISPLAY; this is the comparison.
   *
   * ⚠️ **`NaN`, never `0`, when nothing can be measured.** Zero reads as
   * "Expired", which is a confident wrong answer over a session that may well
   * be live. Callers must test `Number.isNaN` before comparing.
   *
   * This exists so there is ONE source for the session. The deadline lives on
   * the stored record and not on {@link SupportSessionInfo}, so before this the
   * one surface rendering a countdown had to go back to `localStorage` for it —
   * the context saying *whether*, storage saying *until when*, two answers that
   * could disagree.
   */
  supportSessionRemainingMs: () => number;

  /**
   * Start acting as another user. Super admin only; the server is the authority.
   *
   * Rejects if a session is already running (there is no nesting: the base
   * token is what a support session is layered over, and a second layer would
   * shadow the first with nothing able to unwind it).
   *
   * Resolves only once the TARGET's identity and permissions are loaded, so a
   * caller can navigate on the resolved promise without a frame of the super
   * admin's own access rendered under somebody else's name.
   */
  startSupportSession: (userId: number, reason: string) => Promise<void>;

  /**
   * Stop acting as another user and return to your own account.
   *
   * Synchronous and cannot fail — the super admin's own tokens were never
   * touched, so this is a local delete plus a restore of state already held.
   * Telling the server happens in the background.
   */
  endSupportSession: () => void;
}

// Kept in its own module so the provider component and the hook can both import
// it without breaking react-refresh's component-only-export rule.
export const AuthContext = createContext<AuthContextValue | null>(null);
